import React, { cloneElement, useCallback, useMemo } from "react";
import { find, head, map } from "lodash";
import styled from "styled-components";
import {
  Color,
  Elevation,
  FontSize,
  FontWeight,
  GlossyThemeProvider,
  Radius,
  Spacing,
  ZIndexes,
  background,
  border,
  content,
  dominant,
  transparentize,
} from "@glossy";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import Button from "@ui-kit/button";

export const VARIANTS = { primary: dominant, secondary: content };
type Variant = keyof typeof VARIANTS;
type Value = {
  id: string | number;
  label: string | React.ReactNode;
  disabled?: boolean;
};

interface IProps {
  label?: string;
  disclosure?: React.ReactElement | React.ComponentProps<typeof Button>;
  values: Value[];
  selected?: Value["id"];
  onSelect: (id: Value["id"]) => void;
  // Impacts the style of the items, not the disclosure
  variant?: Variant;
  onToggle?: (visible: boolean) => void;
  // Expose visible prop for Storybook and testing
  visible?: boolean;
  disabled?: boolean;
  className?: string;
}

const Root = styled.div<{ disabled: boolean }>`
  pointer-events: ${({ disabled }) => disabled && "none"};
  display: inline-flex;
  align-items: center;

  & > * {
    display: inherit;
  }
`;

const Label = styled.span`
  font-size: ${FontSize.Medium};
  margin-right: ${Spacing.ExtraSmall};
`;

const StyledMenuContent = styled(DropdownMenu.Content)`
  box-shadow: ${Elevation.Elevation_3} !important;
  background-color: ${background};
  border-radius: ${Radius.MediumLarge};
  overflow: hidden;
  padding: ${Spacing.Medium} 0;
  border: 1px solid ${border};
  display: flex;
  flex-direction: column;
  z-index: ${ZIndexes.Top};
`;

const StyledSeparator = styled(DropdownMenu.Separator)`
  height: 1px;
  background-color: ${Color.Black12};
  margin: ${Spacing.ExtraSmall} 0;
`;

const StyledMenuItem = styled(DropdownMenu.Item)<{
  variant: Variant;
  $active: boolean;
  disabled?: boolean;
}>`
  text-align: left;
  line-height: 36px;
  font-size: ${FontSize.Medium};
  font-weight: ${(props) => props.$active && FontWeight.Bold};
  padding: 0 16px;
  cursor: pointer;
  color: ${(props) =>
    props.$active
      ? VARIANTS[props.variant](props)
      : props.disabled
      ? content(props, "tertiary")
      : content};
  background-color: ${(props) =>
    props.$active
      ? transparentize(0.8, VARIANTS[props.variant](props))
      : "initial"};
  :hover {
    background-color: ${(props) =>
      !props.$active && transparentize(0.85, content(props, "primary"))};
  }
  pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
`;

const isReactElement = (
  disclosure: IProps["disclosure"]
): disclosure is React.ReactElement => React.isValidElement(disclosure);

const getSelectedLabel = (
  values: Value[],
  selected?: Value["id"]
): Value | undefined =>
  selected ? find(values, { id: selected }) : head(values);

const useMenu = (values, selected, onSelect) => {
  const selectedValue = useMemo(
    () => getSelectedLabel(values, selected),
    [selected, values]
  );

  const handleSelect = useCallback(
    (value: Value["id"], e: React.MouseEvent) => {
      e.stopPropagation();
      onSelect(value);
    },
    [onSelect]
  );

  return { selectedValue, handleSelect };
};

const UiKitPopoverMenu = React.forwardRef(
  (
    {
      label,
      disclosure,
      values,
      selected,
      onSelect,
      variant = "primary",
      onToggle,
      visible = false,
      disabled = false,
      className,
    }: IProps,
    ref: React.Ref<HTMLDivElement>
  ) => {
    const { selectedValue, handleSelect } = useMenu(values, selected, onSelect);

    return (
      <Root className={className} ref={ref} disabled={disabled}>
        {!!label && <Label>{label}</Label>}
        <DropdownMenu.Root
          onOpenChange={(open) => onToggle && onToggle(open)}
          defaultOpen={visible && !disabled}
        >
          <DropdownMenu.Trigger asChild={true}>
            {isReactElement(disclosure) ? (
              cloneElement(disclosure)
            ) : (
              <Button
                text={(selectedValue || {}).label}
                hasCaret
                isDisabled={disabled}
                {...disclosure}
              />
            )}
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <GlossyThemeProvider name="surface">
              <StyledMenuContent sideOffset={12} align="start">
                {map(values, ({ id, label, disabled }) =>
                  id === "separator" ? (
                    <StyledSeparator key={id} />
                  ) : (
                    <StyledMenuItem
                      key={id}
                      $active={id === selected}
                      variant={variant}
                      onClick={(e) => handleSelect(id, e)}
                      disabled={disabled}
                    >
                      {label}
                    </StyledMenuItem>
                  )
                )}
              </StyledMenuContent>
            </GlossyThemeProvider>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </Root>
    );
  }
);

export default UiKitPopoverMenu;
